/**
 * Usage API
 *
 * RTK Query endpoints for user usage dashboard and admin analytics.
 * Usage records are stored by the backend (usage_records).
 */

import { api } from '../api';

// Types matching backend
export type UsageAction =
  | 'voice_practice_classic'
  | 'voice_practice_live_minute'
  | 'tts_coach_response'
  | 'listen_full_pitch'
  | 'listen_snippet'
  | 'exam_attempt'
  | 'portfolio_profile_view';

export interface RecentUsageRecord {
  action: UsageAction;
  quantity: number;
  /** Safety-allowance units this use took (not credits). */
  unitsConsumed: number;
  occurredAt: string;
}

export interface UserUsageResponse {
  plan: {
    planKey: string;
    planLabel: string;
    capabilities: {
      canUseAI: boolean;
      canUseLiveVoice: boolean;
      canUseTTS: boolean;
      unlimitedPractice: boolean;
      unlimitedListening: boolean;
    };
  };
  /** Monthly usage allowances (LOCAL_NONCOMMERCIAL_SAFETY_LIMIT) — usage limits, not credits. */
  safetyAllowance: {
    authority: 'LOCAL_NONCOMMERCIAL_SAFETY_LIMIT';
    remaining: { practice: number; coaching: number; listen: number };
    monthly: { practice: number; coaching: number; listen: number };
  };
  /** The caller's limits as the server words them; null when they could not be read. */
  limits?: UserLimits | null;
  usage: {
    totalsByAction: Partial<Record<UsageAction, number>>;
    totalUnitsConsumed: number;
    lastUsedAt: string | null;
    recent: RecentUsageRecord[];
  };
}

/** One limit as GET /usage/me describes it (backend services/limitStatus.ts). */
export interface UserLimit {
  key: string;
  title: string;
  summary: string;
  explanation: string;
  period: 'rolling_24h' | 'calendar_day' | 'calendar_month' | 'standing';
  /** 'org': the workspace's limit, shared by everyone in it (e.g. active training modules). */
  scope?: 'user' | 'org';
  /** The plan the number comes from (an org limit's is the workspace's plan, e.g. "Team Basic"). */
  planName?: string;
  max: number;
  /** null: the caller may not see the workspace's count; the limit is shown without usage. */
  used: number | null;
}

export interface UserLimits {
  planName: string;
  enforced: boolean;
  limits: UserLimit[];
}

export interface DailyUsageSummary {
  date: string;
  totalByAction: Partial<Record<UsageAction, number>>;
  totalCredits: number;
}

export interface AdminUsageResponse {
  daily: DailyUsageSummary[];
  totalsByAction: Partial<Record<UsageAction, number>>;
  totalCreditsConsumed: number;
  topUsersByCredits: Array<{ userId: string; totalCredits: number }>;
  meta: {
    logCount: number;
    note: string;
  };
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export const usageApi = api.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Get current user's usage summary.
     * Includes plan info, credits remaining, and recent usage records.
     */
    getMyUsage: builder.query<UserUsageResponse, void>({
      query: () => '/usage/me',
      transformResponse: (response: ApiResponse<UserUsageResponse>) => response.data,
      providesTags: ['Usage'],
    }),

    /**
     * Get system-wide usage analytics (admin only).
     * Includes daily summaries, totals by action, and top users.
     */
    getAdminUsageOverview: builder.query<AdminUsageResponse, void>({
      query: () => '/usage/admin/overview',
      transformResponse: (response: ApiResponse<AdminUsageResponse>) => response.data,
      providesTags: ['AdminUsage'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetMyUsageQuery,
  useGetAdminUsageOverviewQuery,
} = usageApi;

/**
 * Action display labels for UI
 */
export const ACTION_LABELS: Record<UsageAction, string> = {
  voice_practice_classic: 'Classic Practice',
  voice_practice_live_minute: 'Live Voice Practice',
  tts_coach_response: 'Voice Coaching Feedback',
  listen_full_pitch: 'Full Pitch Listen',
  listen_snippet: 'Snippet Preview',
  exam_attempt: 'Certification Exam',
  portfolio_profile_view: 'Profile View',
};

/**
 * Get human-readable label for an action
 */
export function getActionLabel(action: UsageAction): string {
  return ACTION_LABELS[action] || action;
}
